import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { EXPIRY_PRESETS, type ExpiryPresetKey, expiryFromPreset } from "@/lib/expiry";
import { Copy } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { type ApiKeyCreated, DuplicateNameError, useCreateApiKey } from "./mutations";

export function CreateApiKeyDialog({
  projectId,
  open,
  onOpenChange,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [rpm, setRpm] = useState(600);
  const [expiry, setExpiry] = useState<ExpiryPresetKey>("never");
  const [nameError, setNameError] = useState<string | null>(null);
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);
  const create = useCreateApiKey(projectId);

  const submit = async () => {
    setNameError(null);
    try {
      const res = await create.mutateAsync({
        name,
        rate_limit_rpm: rpm,
        expires_at: expiryFromPreset(expiry),
      });
      setCreated(res);
    } catch (e) {
      if (e instanceof DuplicateNameError) {
        setNameError(e.message);
        return;
      }
      toast.error(`Create key failed: ${(e as Error).message}`);
    }
  };

  const close = () => {
    onOpenChange(false);
    setCreated(null);
    setName("");
    setRpm(600);
    setExpiry("never");
    setNameError(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) close();
      }}
    >
      <DialogContent>
        {!created ? (
          <>
            <DialogTitle>Create API key</DialogTitle>
            <DialogDescription>
              ⚠ The key secret will be shown ONCE after creation — store it safely.
            </DialogDescription>
            <div className="mt-4 space-y-3">
              {/* biome-ignore lint/a11y/noLabelWithoutControl: Input is the control */}
              <label className="block">
                <span className="text-xs text-fg-muted">Name</span>
                <Input
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (nameError) setNameError(null);
                  }}
                  autoFocus
                  placeholder="my-api-key"
                  aria-invalid={nameError !== null}
                />
                {nameError && (
                  <span role="alert" className="mt-1 block text-[11px] text-rose-700">
                    {nameError}
                  </span>
                )}
              </label>
              {/* biome-ignore lint/a11y/noLabelWithoutControl: Input is the control */}
              <label className="block">
                <span className="text-xs text-fg-muted">Rate limit (rpm)</span>
                <Input type="number" value={rpm} onChange={(e) => setRpm(Number(e.target.value))} />
              </label>
              <div className="block">
                <span className="text-xs text-fg-muted">Expires</span>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {EXPIRY_PRESETS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      aria-pressed={expiry === p.key}
                      onClick={() => setExpiry(p.key)}
                      className={`rounded border px-2 py-1 text-[11px] font-medium ${
                        expiry === p.key
                          ? "border-accent bg-accent/10 text-fg"
                          : "border-border bg-surface text-fg-muted hover:text-fg"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <DialogClose asChild>
                <Button variant="default">Cancel</Button>
              </DialogClose>
              <Button variant="primary" disabled={!name || create.isPending} onClick={submit}>
                Create
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogTitle>Key created</DialogTitle>
            <DialogDescription>Copy this secret now. It cannot be shown again.</DialogDescription>
            <div className="mt-4 flex items-center gap-2 rounded-md border border-border bg-surface-2 px-3 py-2">
              <span className="mono flex-1 break-all text-xs">{created.raw_key}</span>
              <Button
                size="icon"
                variant="ghost"
                aria-label="Copy"
                onClick={async () => {
                  await navigator.clipboard.writeText(created.raw_key);
                  toast.success("Copied");
                }}
              >
                <Copy size={13} />
              </Button>
            </div>
            <div className="mt-5 flex justify-end">
              <Button variant="primary" onClick={close}>
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
